/*
* === ContractManagerPage.js (Final - Modal Alerts & Notifications) ===
* Features:
* - CRUD for Contract Templates
* - Clone System Templates
* - Rich Text Editor
* - FIX: Replaced browser alerts with Snackbar and Dialogs
*/
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { 
    Container, Typography, Card, Box, Alert, Button, Grid, Paper, 
    CircularProgress, IconButton, Dialog, DialogTitle, DialogContent, 
    DialogActions, TextField, List, ListItem, ListItemText, Divider, 
    Chip, FormControl, InputLabel, Select, MenuItem, Tooltip,
    Accordion, AccordionSummary, AccordionDetails, Snackbar, DialogContentText
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

// Icons
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GavelIcon from '@mui/icons-material/Gavel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BusinessIcon from '@mui/icons-material/Business';
import PublicIcon from '@mui/icons-material/Public';
import WarningIcon from '@mui/icons-material/Warning';

// Editor
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const CONTRACT_TYPES = ['Agreement', 'Contract', 'Consent', 'Guarantor', 'MoA'];
const VARIABLES = [
    { label: 'Applicant Name', code: '{{ApplicantName}}' },
    { label: 'ID Number', code: '{{IdNumber}}' },
    { label: 'Funder Name', code: '{{FunderName}}' },
    { label: 'Amount', code: '{{Amount}}' },
    { label: 'Date', code: '{{Date}}' }
];

const ContractManagerPage = () => {
    const { token, user } = useAuth();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // UI State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [isCloneConfirmOpen, setIsCloneConfirmOpen] = useState(false);
    
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [actionTemplate, setActionTemplate] = useState(null); // Template targeted for delete/clone
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Notification State (Replaces alerts)
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    
    const quillRef = useRef(null);

    const fetchTemplates = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/contract-templates', { headers: { Authorization: `Bearer ${token}` } });
            setTemplates(res.data);
        } catch (err) { 
            setError('Failed to load templates.'); 
        } finally { 
            setLoading(false); 
        }
    }, [token]);

    useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

    const grouped = useMemo(() => {
        const system = templates.filter(t => t.CompanyId === null);
        const mine = templates.filter(t => t.CompanyId !== null); 
        return { 'System Templates (Read-Only/Clone)': system, 'My Organization Contracts': mine };
    }, [templates]);

    // Helper for Toast Notifications
    const showToast = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleSave = async () => {
        if (!selectedTemplate.TemplateName || !selectedTemplate.Body) {
            showToast('Please fill in all required fields.', 'warning');
            return;
        }

        setIsSubmitting(true);
        try {
            const method = selectedTemplate.Id ? 'put' : 'post';
            const url = selectedTemplate.Id ? `/admin/contract-templates/${selectedTemplate.Id}` : '/admin/contract-templates';
            await api[method](url, selectedTemplate, { headers: { Authorization: `Bearer ${token}` } });
            
            fetchTemplates(); 
            setIsModalOpen(false);
            showToast(selectedTemplate.Id ? 'Contract updated.' : 'Contract created.');
        } catch (e) { 
            showToast('Failed to save contract.', 'error'); 
        } finally { 
            setIsSubmitting(false); 
        }
    };

    // --- CLONE LOGIC ---
    const handleOpenClone = (tpl) => {
        setActionTemplate(tpl);
        setIsCloneConfirmOpen(true);
    };

    const handleConfirmClone = async () => {
        if (!actionTemplate) return;
        try {
            await api.post(`/admin/contract-templates/${actionTemplate.Id}/clone`, {}, { headers: { Authorization: `Bearer ${token}` } });
            fetchTemplates();
            showToast('Template cloned successfully.');
        } catch (e) { 
            showToast('Failed to clone template.', 'error'); 
        } finally {
            setIsCloneConfirmOpen(false);
            setActionTemplate(null);
        }
    };

    // --- DELETE LOGIC ---
    const handleOpenDelete = (tpl) => {
        setActionTemplate(tpl);
        setIsDeleteConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!actionTemplate) return;
        try {
            await api.delete(`/admin/contract-templates/${actionTemplate.Id}`, { headers: { Authorization: `Bearer ${token}` } });
            fetchTemplates();
            showToast('Template deleted.');
        } catch(e) { 
            showToast('Failed to delete template.', 'error'); 
        } finally {
            setIsDeleteConfirmOpen(false);
            setActionTemplate(null);
        }
    };

    const insertVariable = (code) => {
        const editor = quillRef.current?.getEditor();
        if (editor) editor.insertText(editor.getSelection(true)?.index || 0, code);
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h4" fontWeight="bold">Contract Management</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setSelectedTemplate({ TemplateName: '', Subject: '', Body: '', Type: 'Agreement' }); setIsModalOpen(true); }}>
                    Create Contract
                </Button>
            </Box>

            {loading ? <CircularProgress /> : Object.entries(grouped).map(([group, list]) => (
                <Accordion key={group} defaultExpanded sx={{ mb: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'action.hover' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {group.includes('System') ? <PublicIcon color="secondary" sx={{ mr: 1 }} /> : <BusinessIcon color="primary" sx={{ mr: 1 }} />}
                            <Typography variant="h6">{group}</Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                        <List>
                            {list.length === 0 && <ListItem><ListItemText secondary="No templates found." /></ListItem>}
                            {list.map((t) => (
                                <React.Fragment key={t.Id}>
                                    <ListItem>
                                        <ListItemText 
                                            primary={t.TemplateName} 
                                            secondary={<Typography variant="caption">{t.Type} • {new Date(t.CreatedAt).toLocaleDateString()}</Typography>} 
                                        />
                                        <Box>
                                            {t.CompanyId === null && (
                                                <Tooltip title="Clone to My Company">
                                                    <IconButton onClick={() => handleOpenClone(t)} color="secondary"><ContentCopyIcon /></IconButton>
                                                </Tooltip>
                                            )}
                                            {(t.CompanyId !== null || user.role === 'SuperUser') && (
                                                <>
                                                    <Tooltip title="Edit"><IconButton onClick={() => { setSelectedTemplate(t); setIsModalOpen(true); }} color="primary"><EditIcon /></IconButton></Tooltip>
                                                    <Tooltip title="Delete"><IconButton onClick={() => handleOpenDelete(t)} color="error"><DeleteIcon /></IconButton></Tooltip>
                                                </>
                                            )}
                                        </Box>
                                    </ListItem>
                                    <Divider />
                                </React.Fragment>
                            ))}
                        </List>
                    </AccordionDetails>
                </Accordion>
            ))}

            {/* Editor Modal */}
            <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>{selectedTemplate?.Id ? 'Edit Contract' : 'New Contract'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={8}>
                            <TextField fullWidth label="Template Name" value={selectedTemplate?.TemplateName || ''} onChange={e => setSelectedTemplate({...selectedTemplate, TemplateName: e.target.value})} />
                        </Grid>
                        <Grid item xs={4}>
                            <FormControl fullWidth>
                                <InputLabel>Type</InputLabel>
                                <Select value={selectedTemplate?.Type || 'Agreement'} label="Type" onChange={e => setSelectedTemplate({...selectedTemplate, Type: e.target.value})}>
                                    {CONTRACT_TYPES.map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Subject / Title" value={selectedTemplate?.Subject || ''} onChange={e => setSelectedTemplate({...selectedTemplate, Subject: e.target.value})} />
                        </Grid>
                    </Grid>
                    
                    <Box sx={{ mt: 2, mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {VARIABLES.map(v => <Chip key={v.code} label={v.label} size="small" onClick={() => insertVariable(v.code)} clickable />)}
                    </Box>

                    <ReactQuill 
                        ref={quillRef} 
                        theme="snow" 
                        value={selectedTemplate?.Body || ''} 
                        onChange={v => setSelectedTemplate({...selectedTemplate, Body: v})} 
                        style={{ height: 300, marginBottom: 50 }} 
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={isSubmitting}>Save</Button>
                </DialogActions>
            </Dialog>

            {/* DELETE CONFIRM MODAL */}
            <Dialog open={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)}>
                <DialogTitle sx={{ color: 'error.main', display: 'flex', alignItems: 'center' }}>
                    <WarningIcon sx={{ mr: 1 }} /> Confirm Deletion
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete <strong>{actionTemplate?.TemplateName}</strong>?
                    </DialogContentText>
                    <Alert severity="error" sx={{ mt: 2 }}>This cannot be undone.</Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDeleteConfirmOpen(false)}>Cancel</Button>
                    <Button onClick={handleConfirmDelete} variant="contained" color="error">Delete</Button>
                </DialogActions>
            </Dialog>

            {/* CLONE CONFIRM MODAL */}
            <Dialog open={isCloneConfirmOpen} onClose={() => setIsCloneConfirmOpen(false)}>
                <DialogTitle>Confirm Clone</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Copy <strong>{actionTemplate?.TemplateName}</strong> to your company templates?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsCloneConfirmOpen(false)}>Cancel</Button>
                    <Button onClick={handleConfirmClone} variant="contained">Clone</Button>
                </DialogActions>
            </Dialog>

            {/* NOTIFICATION SNACKBAR */}
            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={6000} 
                onClose={() => setSnackbar({ ...snackbar, open: false })} 
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

        </Container>
    );
};

export default ContractManagerPage;